/**
 * Base API service for the Admin Panel.
 * Handles fetch requests, headers, and unified error handling.
 */
import { config } from '../config.js';
import { notify, sessionChannel } from '../stores/index.js';

const host = config.host;

class ApiService {
    #isRedirecting = false;

    /**
     * Generic fetch wrapper
     * @param {string} url - Full URL or endpoint
     * @param {object} options - Fetch options
     */
    async request(url, options = {}) {
        // Si la URL no tiene http, asumimos que es relativa al host
        const fullUrl = url.startsWith('http') ? url : `${host}${url.startsWith('/') ? '' : '/'}${url}`;
        
        const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
        
        const isFormData = options.body instanceof FormData;

        const defaultHeaders = {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };

        const fetchConfig = {
            ...options,
            headers: {
                ...defaultHeaders,
                ...options.headers
            }
        };

        try {
            const response = await fetch(fullUrl, fetchConfig);
            // Intentamos parsear, si falla (204 No Content), devolvemos objeto vacío
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                // Guardian de Sesión: Si el error es por falta de autorización, cerramos sesión
                if (data.message?.startsWith('auth.unauthorized') && !this.#isRedirecting) {
                    this.#isRedirecting = true;
                    notify('Sesión inválida, saliendo del sistema...', 'error');
                    
                    setTimeout(async () => {
                        sessionStorage.clear();
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('_b');
                        
                        sessionChannel.send({
                            user: null,
                            token: null,
                            isAuthenticated: false,
                            subscription: null
                        });
                        
                        const { contextChannel: ctx } = await import('../stores/index.js');
                        ctx.send({ branchId: null });

                        this.#isRedirecting = false;
                    }, 6000);
                }

                const error = new Error(data.message || 'Error en la petición');
                error.status = response.status;
                error.data = data;
                throw error;
            }

            return data;
        } catch (error) {
            console.error(`[API Error] ${url}:`, error);
            throw error;
        }
    }

    async get(url, params = {}) {
        // Simple query string builder
        const queryString = new URLSearchParams(params).toString();
        const fullUrl = queryString ? `${url}?${queryString}` : url;
        return this.request(fullUrl, { method: 'GET' });
    }

    async post(url, data = {}) {
        const isFormData = data instanceof FormData;
        return this.request(url, {
            method: 'POST',
            body: isFormData ? data : JSON.stringify(data)
        });
    }

    async patch(url, data = {}) {
        return this.request(url, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    async put(url, data = {}) {
        return this.request(url, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async delete(url) {
        return this.request(url, { method: 'DELETE' });
    }
}

export const api = new ApiService();
